import type { TFunction } from "i18next";
import { DateTime } from "luxon";

import type {
  CertificationProgress,
  ModuleProgress,
} from "@/redux/certifications/types";
import {
  KNOWLEDGE_CHECK_STATES,
  MODULE_STATUSES,
} from "@/domains/certifications/constants";
import { toKnowledgeCheckState } from "@/domains/certifications/knowledgeCheck";
import type { ModuleOverview } from "@/domains/certifications/types";
import type {
  CheckRow,
  WeekPill,
} from "@/features/certifications/participant-detail/types";
import { WEEK_PILLS } from "@/features/certifications/participant-detail/constants";

const SHORT_DATE_FORMAT = "d MMM yyyy";

export const toWeekPill = ({ status }: ModuleOverview): WeekPill => {
  if (status === MODULE_STATUSES.COMPLETE) {
    return WEEK_PILLS.COMPLETE;
  }
  return status === MODULE_STATUSES.CURRENT
    ? WEEK_PILLS.CURRENT
    : WEEK_PILLS.FUTURE;
};

export const getCompletedAt = (moduleProgress: ModuleProgress | undefined) => {
  const dates = (moduleProgress?.requirements ?? [])
    .map(({ satisfiedAt }) => satisfiedAt)
    .filter((date): date is string => date !== null)
    .map((date) => DateTime.fromISO(date));
  return dates.length > 0 ? DateTime.max(...dates) : null;
};

export const getWeekStatusLine = (
  t: TFunction,
  week: ModuleOverview,
  moduleProgress: ModuleProgress | undefined,
) => {
  if (week.status === MODULE_STATUSES.COMPLETE) {
    const completedAt = getCompletedAt(moduleProgress);
    return completedAt
      ? t("certification.admin.participant.week.completedOn", {
          date: completedAt.toFormat(SHORT_DATE_FORMAT),
        })
      : t("certification.admin.participant.week.completed");
  }
  if (week.status === MODULE_STATUSES.LOCKED) {
    return t("certification.admin.participant.week.opensAfter", {
      week: week.position - 1,
    });
  }
  if (week.knowledgeCheck?.checkState === KNOWLEDGE_CHECK_STATES.UNDER_REVIEW) {
    return t("certification.admin.participant.week.checkUnderReview");
  }
  return week.isStarted
    ? t("certification.admin.participant.week.inProgress")
    : t("certification.admin.participant.week.notStarted");
};

export const getCheckRows = (
  weeks: ModuleOverview[],
  progress: CertificationProgress,
): CheckRow[] =>
  weeks.flatMap((week) => {
    if (!week.knowledgeCheck) {
      return [];
    }
    const items =
      progress.modules.find(({ id }) => id === week.module.id)?.items ?? [];
    const attempt =
      items.find((item) => item.knowledgeCheck !== null)?.knowledgeCheck ??
      null;
    return [
      {
        week: week.position,
        title: week.module.title,
        state: week.knowledgeCheck.isPassed
          ? KNOWLEDGE_CHECK_STATES.PASSED
          : toKnowledgeCheckState(
              attempt?.state ?? null,
              attempt?.attemptsRemaining ?? 0,
            ),
        attempt,
      },
    ];
  });
